import type { ReactNode } from 'react';
import AgentMark, { type AgentKey } from './AgentMark';
import VerificationRule from './VerificationRule';
import { VERIFICATION_LABELS, type LegendaLang, type Verification } from './vocabulary';

export interface ProvenanceGutterProps {
  /** Which specialist produced this block. */
  agent: AgentKey;
  lang: LegendaLang;
  /** Omitted for blocks that carry no single value — routing notes, refusals. */
  verification?: Verification;
  /** Short source locator, e.g. `1982 · с. 118`. */
  citation?: string;
  children: ReactNode;
}

/**
 * The layout invariant of the whole product.
 *
 * Every evidence-bearing block is laid out as a fixed left column plus content.
 * The column holds the agent code, the status label and the source locator; the
 * content carries the verification rule on its own left edge. Reading straight
 * down the left side of an answer therefore gives the provenance of the entire
 * argument, and no inline badge ever has to interrupt a sentence.
 *
 * Below 720px the gutter becomes a horizontal strip above the content. It is
 * never dropped: on a phone the detail is reduced, the status semantics are not.
 */
export default function ProvenanceGutter({
  agent,
  lang,
  verification,
  citation,
  children,
}: ProvenanceGutterProps) {
  return (
    <div className="lg-pv">
      <div className="lg-pv__gutter">
        <AgentMark agent={agent} />
        {verification ? (
          <span className="lg-pv__label">{VERIFICATION_LABELS[verification][lang]}</span>
        ) : null}
        {citation ? <span className="lg-pv__label">{citation}</span> : null}
      </div>

      {verification ? (
        <VerificationRule status={verification} lang={lang} labelInLegend className="lg-pv__body">
          {children}
        </VerificationRule>
      ) : (
        <div className="lg-pv__body">{children}</div>
      )}
    </div>
  );
}
